import React from 'react';

import { observer } from 'mobx-react';

import { Skeleton } from '@mui/material';

import { SmallDataError } from 'containers/Error/DataError/styles';

import HistoryFeed from 'components/HistoryFeed';

import { useStore } from 'utils/hooks/store';

import HistoryEntryRow from './HistoryEntryRow';
import { groupHistoryByDay } from './history';
import { HistoryBody, HistoryCard, HistoryEmpty } from './styles';

const SKELETON_ROWS = 4;

const GroupHistoryTab: React.FunctionComponent = observer(() => {
  const { memberGroupDetails } = useStore();
  const { group, historyEntries, historyLoaded, historyLoading, historyIsFailure, hasMoreHistory } = memberGroupDetails;

  React.useEffect(() => {
    if (group && !historyLoaded && !historyLoading && !historyIsFailure) {
      memberGroupDetails.loadHistory(0);
    }
  }, [group?.uuid, historyLoaded]);

  if (!historyLoaded && historyLoading) {
    return (
      <HistoryCard data-testid="group-history-tab">
        <HistoryBody>
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} height={48} />
          ))}
        </HistoryBody>
      </HistoryCard>
    );
  }

  if (historyIsFailure && historyEntries.length === 0) {
    return (
      <HistoryCard data-testid="group-history-tab">
        <SmallDataError
          data-testid="group-history-error"
          onReload={() => memberGroupDetails.loadHistory(0)}
          message="We are unable to load history"
        />
      </HistoryCard>
    );
  }

  if (historyEntries.length === 0) {
    return (
      <HistoryCard data-testid="group-history-tab">
        <HistoryBody>
          <HistoryEmpty data-testid="group-history-empty">No membership changes recorded yet.</HistoryEmpty>
        </HistoryBody>
      </HistoryCard>
    );
  }

  return (
    <HistoryCard data-testid="group-history-tab">
      <HistoryBody>
        <HistoryFeed
          days={groupHistoryByDay(historyEntries)}
          getKey={(entry) => entry.uuid}
          renderEntry={(entry) => <HistoryEntryRow entry={entry} groupUuid={group?.uuid} />}
          hasMore={hasMoreHistory}
          loadingMore={historyLoading}
          onLoadMore={() => memberGroupDetails.loadHistory(memberGroupDetails.historyEntries.length)}
          loadMoreTestId="group-history-load-more"
        />
      </HistoryBody>
    </HistoryCard>
  );
});

export default GroupHistoryTab;
